import { useState, type DragEvent } from 'react'
import { Link, useParams } from 'react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowDown,
  ArrowUp,
  GripVertical,
  Layers,
  Lock,
  Pencil,
  Plus,
  Trash2,
  Unlock,
} from 'lucide-react'
import {
  ABSENCE_POLICIES,
  GRADING_TYPES,
  STAGE_POPULATIONS,
  STAGE_PUBLICATIONS,
  type StageDisciplineInfo,
  type StageInfo,
} from '@concours/shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmModal,
  Empty,
  InfoGrid,
  InfoItem,
  PageHeader,
  Tabs,
  useAuth,
  useCan,
  useToast,
} from '@concours/ui'
import { errorMessage } from '../../lib/errors'
import { when } from '../../lib/competition-types'

import { SELECTION_KEY, useSelection } from '../../lib/selection'
import { StageModal } from './StageModal'
import { DisciplineModal } from './DisciplineModal'
import { RulesPanel } from './RulesPanel'

type Panel = 'params' | 'disciplines' | 'rules'
const STATUS_VARIANT = { draft: 'neutral', grading: 'warning', closed: 'success' } as const
const fmt = (n: number) => n.toLocaleString('fr-FR', { maximumFractionDigits: 2 })

/**
 * Configurateur du parcours de sélection d'un concours (TODO phase 13) :
 * étapes ordonnées (glisser-déposer ou flèches), disciplines et barèmes,
 * règles de passage versionnées, verrouillage des barèmes.
 */
export function StagesPage() {
  const { competitionId = '' } = useParams()
  const { session } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const query = useSelection(competitionId)
  const scope = { type: 'competition' as const, id: competitionId }
  const canConfigure = useCan('selection.configure', scope)
  const canUnlock = useCan('selection.unlock_scales', scope)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [panel, setPanel] = useState<Panel>('params')
  const [stageModal, setStageModal] = useState<'new' | StageInfo | null>(null)
  const [disciplineModal, setDisciplineModal] = useState<'new' | StageDisciplineInfo | null>(null)
  const [confirm, setConfirm] = useState<'delete' | 'lock' | 'unlock' | null>(null)
  const [dragged, setDragged] = useState<string | null>(null)

  const stages = query.data?.stages ?? []
  const selected = stages.find((s) => s.id === selectedId) ?? stages[0] ?? null
  const invalidate = () => queryClient.invalidateQueries({ queryKey: SELECTION_KEY })

  const reorder = useMutation({
    mutationFn: (ids: string[]) =>
      session.api.put(`/admin/competitions/${competitionId}/stages/order`, { ids }),
    onSuccess: async () => {
      toast.success('Ordre des étapes enregistré')
      await invalidate()
    },
    onError: (error) => toast.error(errorMessage(error)),
  })
  const action = useMutation({
    mutationFn: async ({
      kind,
      reason,
    }: {
      kind: 'delete' | 'lock' | 'unlock'
      reason?: string
    }) => {
      if (!selected) return
      if (kind === 'delete') await session.api.delete(`/admin/stages/${selected.id}`)
      if (kind === 'lock') await session.api.post(`/admin/stages/${selected.id}/lock`, {})
      if (kind === 'unlock')
        await session.api.post(`/admin/stages/${selected.id}/unlock`, { reason })
    },
    onSuccess: async (_, { kind }) => {
      toast.success(
        kind === 'delete'
          ? 'Étape supprimée'
          : kind === 'lock'
            ? 'Barèmes verrouillés : la notation peut commencer'
            : 'Barèmes déverrouillés (procédure exceptionnelle journalisée)'
      )
      if (kind === 'delete') setSelectedId(null)
      setConfirm(null)
      await invalidate()
    },
    onError: (error) => toast.error(errorMessage(error)),
  })
  const removeDiscipline = useMutation({
    mutationFn: (d: StageDisciplineInfo) =>
      session.api.delete(`/admin/stages/${selected!.id}/disciplines/${d.id}`),
    onSuccess: async () => {
      toast.success('Discipline retirée de l’étape')
      await invalidate()
    },
    onError: (error) => toast.error(errorMessage(error)),
  })

  const move = (from: number, to: number) => {
    if (to < 0 || to >= stages.length || from === to) return
    const ids = stages.map((s) => s.id)
    const [id] = ids.splice(from, 1)
    ids.splice(to, 0, id!)
    reorder.mutate(ids)
  }
  const onDrop = (event: DragEvent, target: number) => {
    event.preventDefault()
    const from = stages.findIndex((s) => s.id === dragged)
    setDragged(null)
    if (from >= 0) move(from, target)
  }
  const anyLocked = stages.some((s) => s.status !== 'draft')

  if (query.isError) return <Alert tone="danger">{errorMessage(query.error)}</Alert>
  const c = query.data?.competition

  return (
    <>
      <PageHeader
        eyebrow={
          <Link to="/selection" className="link">
            Sélection
          </Link>
        }
        title={c ? `Parcours — ${c.title}` : 'Parcours de sélection'}
        description={
          c ? (
            <>
              <span className="mono">{c.reference}</span> · {c.session} · {c.seats} places
            </>
          ) : undefined
        }
        actions={
          canConfigure && (
            <Button variant="primary" icon={<Plus />} onClick={() => setStageModal('new')}>
              Ajouter une étape
            </Button>
          )
        }
      />

      {stages.length > 0 && (
        <Journey stages={stages} selected={selected?.id} onSelect={setSelectedId} />
      )}

      {query.isPending ? (
        <Card>
          <CardBody>Chargement…</CardBody>
        </Card>
      ) : stages.length === 0 ? (
        <Card>
          <Empty
            icon={<Layers aria-hidden="true" />}
            title="Aucune étape configurée"
            description="Commencez par l’épreuve écrite, l’épreuve physique, l’oral ou une vérification (administrative, médicale)."
            action={
              canConfigure && (
                <Button variant="primary" icon={<Plus />} onClick={() => setStageModal('new')}>
                  Ajouter la première étape
                </Button>
              )
            }
          />
        </Card>
      ) : (
        <div className="sel-layout">
          <Card className="sel-stages">
            <CardHeader title="Étapes" />
            <ol className="sel-stage-list" aria-label="Étapes du parcours, dans l’ordre">
              {stages.map((s, i) => (
                <li
                  key={s.id}
                  className="sel-stage-item"
                  data-active={s.id === selected?.id || undefined}
                  data-dragging={s.id === dragged || undefined}
                  draggable={canConfigure && !anyLocked}
                  onDragStart={() => setDragged(s.id)}
                  onDragEnd={() => setDragged(null)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => onDrop(e, i)}
                >
                  {canConfigure && !anyLocked && (
                    <GripVertical className="sel-grip" aria-hidden="true" size={16} />
                  )}
                  <button
                    type="button"
                    className="sel-stage-btn"
                    onClick={() => setSelectedId(s.id)}
                  >
                    <span className="sel-rank">{s.position}</span>
                    <span className="sel-stage-text">
                      <strong>{s.label}</strong>
                      <span className="muted">
                        {s.kindLabel} ·{' '}
                        {s.graded ? `${s.disciplines.length} discipline(s)` : 'par statut'}
                      </span>
                      <span>
                        <Badge variant={STATUS_VARIANT[s.status]}>{s.statusLabel}</Badge>
                      </span>
                    </span>
                  </button>
                  {canConfigure && !anyLocked && (
                    <span className="sel-stage-move">
                      <Button
                        variant="ghost"
                        size="sm"
                        iconOnly
                        icon={<ArrowUp />}
                        aria-label={`Monter « ${s.label} »`}
                        disabled={i === 0 || reorder.isPending}
                        onClick={() => move(i, i - 1)}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        iconOnly
                        icon={<ArrowDown />}
                        aria-label={`Descendre « ${s.label} »`}
                        disabled={i === stages.length - 1 || reorder.isPending}
                        onClick={() => move(i, i + 1)}
                      />
                    </span>
                  )}
                </li>
              ))}
            </ol>
            {anyLocked && canConfigure && (
              <p className="muted sel-note">
                Une étape est en notation : l’ordre du parcours est figé.
              </p>
            )}
          </Card>

          {selected && (
            <div className="stack" style={{ gap: 'var(--space-4)', minWidth: 0 }}>
              <Card>
                <CardHeader
                  title={`Étape ${selected.position} — ${selected.label}`}
                  actions={
                    <>
                      {canConfigure && (
                        <Button size="sm" icon={<Pencil />} onClick={() => setStageModal(selected)}>
                          Modifier
                        </Button>
                      )}
                      {canConfigure && selected.status === 'draft' && (
                        <Button
                          size="sm"
                          variant="primary"
                          icon={<Lock />}
                          disabled={selected.readiness.length > 0}
                          onClick={() => setConfirm('lock')}
                        >
                          Verrouiller les barèmes
                        </Button>
                      )}
                      {canUnlock && selected.status === 'grading' && (
                        <Button size="sm" icon={<Unlock />} onClick={() => setConfirm('unlock')}>
                          Déverrouiller
                        </Button>
                      )}
                      {canConfigure && selected.status === 'draft' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          iconOnly
                          icon={<Trash2 />}
                          aria-label="Supprimer l’étape"
                          onClick={() => setConfirm('delete')}
                        />
                      )}
                    </>
                  }
                />
                <CardBody>
                  {selected.status === 'draft' && selected.readiness.length > 0 && (
                    <Alert tone="warning" title="Avant d’ouvrir la notation">
                      <ul className="sel-issues">
                        {selected.readiness.map((r) => (
                          <li key={r.field + r.message}>{r.message}</li>
                        ))}
                      </ul>
                    </Alert>
                  )}
                  {selected.status === 'draft' && selected.readiness.length === 0 && (
                    <Alert tone="success">
                      {canConfigure
                        ? 'Étape prête : verrouillez les barèmes pour ouvrir la notation.'
                        : 'Étape prête pour la notation.'}
                    </Alert>
                  )}
                  {selected.status !== 'draft' && (
                    <Alert tone="info">
                      Barèmes verrouillés le {when(selected.scalesLockedAt)}. Disciplines, barèmes
                      et règles ne sont plus modifiables.
                    </Alert>
                  )}
                </CardBody>
              </Card>

              <Tabs
                label="Configuration de l’étape"
                value={panel}
                onChange={(v) => setPanel(v as Panel)}
                items={[
                  { value: 'params', label: 'Paramètres' },
                  ...(selected.graded
                    ? [
                        {
                          value: 'disciplines',
                          label: `Disciplines · ${selected.disciplines.length}`,
                        },
                      ]
                    : []),
                  { value: 'rules', label: 'Règles de passage' },
                ]}
              />

              {panel === 'params' && <StageParams stage={selected} />}
              {panel === 'disciplines' && selected.graded && (
                <DisciplinesCard
                  stage={selected}
                  editable={canConfigure && selected.status === 'draft'}
                  onAdd={() => setDisciplineModal('new')}
                  onEdit={setDisciplineModal}
                  onRemove={(d) => removeDiscipline.mutate(d)}
                />
              )}
              {(panel === 'rules' || (panel === 'disciplines' && !selected.graded)) && (
                <RulesPanel key={selected.id} stage={selected} canConfigure={canConfigure} />
              )}
            </div>
          )}
        </div>
      )}

      {stageModal && (
        <StageModal
          competitionId={competitionId}
          stage={stageModal === 'new' ? null : stageModal}
          first={stageModal === 'new' ? stages.length === 0 : stageModal.position === 1}
          onClose={() => setStageModal(null)}
        />
      )}
      {disciplineModal && selected && (
        <DisciplineModal
          stage={selected}
          current={disciplineModal === 'new' ? null : disciplineModal}
          onClose={() => setDisciplineModal(null)}
        />
      )}
      <ConfirmModal
        open={confirm === 'delete'}
        onClose={() => setConfirm(null)}
        tone="danger"
        title="Supprimer cette étape ?"
        message="Ses disciplines et versions de règles sont supprimées. Les étapes suivantes remontent d’un rang."
        confirmLabel="Supprimer"
        onConfirm={() => action.mutateAsync({ kind: 'delete' })}
      />
      <ConfirmModal
        open={confirm === 'lock'}
        onClose={() => setConfirm(null)}
        title="Verrouiller les barèmes ?"
        message="La notation s’ouvre : disciplines, barèmes et règles deviennent non modifiables. Un déverrouillage reste possible, de façon exceptionnelle, avec un motif journalisé."
        confirmLabel="Verrouiller"
        onConfirm={() => action.mutateAsync({ kind: 'lock' })}
      />
      <ConfirmModal
        open={confirm === 'unlock'}
        onClose={() => setConfirm(null)}
        tone="danger"
        title="Déverrouiller les barèmes ?"
        message="Procédure exceptionnelle : l’étape repasse en préparation. Le motif est inscrit au journal d’audit."
        confirmLabel="Déverrouiller"
        reasonRequired
        reasonLabel="Motif du déverrouillage"
        reasonMinLength={10}
        onConfirm={(reason) => action.mutateAsync({ kind: 'unlock', reason })}
      />
    </>
  )
}

/** Aperçu visuel du parcours (étape → étape). */
function Journey({
  stages,
  selected,
  onSelect,
}: {
  stages: StageInfo[]
  selected?: string
  onSelect: (id: string) => void
}) {
  return (
    <Card className="sel-journey-card">
      <CardHeader title="Aperçu du parcours" />
      <ol className="sel-journey" aria-label="Aperçu du parcours">
        <li className="sel-journey-step sel-journey-start">
          <span className="sel-journey-dot" aria-hidden="true" />
          <span>Dossiers recevables</span>
        </li>
        {stages.map((s) => (
          <li key={s.id} className="sel-journey-step" data-active={s.id === selected || undefined}>
            <button type="button" onClick={() => onSelect(s.id)}>
              <span className="sel-journey-dot" data-status={s.status} aria-hidden="true">
                {s.position}
              </span>
              <span className="sel-journey-label">{s.label}</span>
              <span className="muted">
                {s.kindLabel}
                {s.startsAt ? ` · ${new Date(s.startsAt).toLocaleDateString('fr-FR')}` : ''}
              </span>
            </button>
          </li>
        ))}
        <li className="sel-journey-step sel-journey-end">
          <span className="sel-journey-dot" aria-hidden="true" />
          <span>Résultats définitifs</span>
        </li>
      </ol>
    </Card>
  )
}

function StageParams({ stage }: { stage: StageInfo }) {
  return (
    <Card>
      <CardBody>
        <InfoGrid>
          <InfoItem label="Type">{stage.kindLabel}</InfoItem>
          <InfoItem label="Notation">
            {stage.graded ? 'Notée (disciplines)' : 'Par statut'}
          </InfoItem>
          <InfoItem label="Population">{STAGE_POPULATIONS[stage.population]}</InfoItem>
          <InfoItem label="Places / quota">{stage.seats ?? '—'}</InfoItem>
          <InfoItem label="Convocations">{when(stage.convocationAt)}</InfoItem>
          <InfoItem label="Début">{when(stage.startsAt)}</InfoItem>
          <InfoItem label="Fin">{when(stage.endsAt)}</InfoItem>
          <InfoItem label="Publication">{STAGE_PUBLICATIONS[stage.publication]}</InfoItem>
          <InfoItem label="Circuit de validation">
            {stage.commissionValidation
              ? 'Validation par la commission'
              : 'Sans validation intermédiaire'}
          </InfoItem>
          {!stage.graded && (
            <InfoItem label="Statuts">
              {stage.statuses.map((s) => (
                <Badge
                  key={s.code}
                  variant={s.passes ? 'success' : 'neutral'}
                  style={{ marginRight: 4 }}
                >
                  {s.label}
                </Badge>
              ))}
            </InfoItem>
          )}
        </InfoGrid>
        {stage.convocationTemplate && (
          <details className="sel-template">
            <summary>Modèle de convocation</summary>
            <p>{stage.convocationTemplate}</p>
          </details>
        )}
      </CardBody>
    </Card>
  )
}

function DisciplinesCard({
  stage,
  editable,
  onAdd,
  onEdit,
  onRemove,
}: {
  stage: StageInfo
  editable: boolean
  onAdd: () => void
  onEdit: (d: StageDisciplineInfo) => void
  onRemove: (d: StageDisciplineInfo) => void
}) {
  const totalCoef = stage.disciplines.reduce(
    (n, d) => n + (d.grading === 'status' ? 0 : d.coefficient),
    0
  )
  return (
    <Card>
      <CardHeader
        title="Disciplines et barèmes"
        actions={
          editable && (
            <Button size="sm" variant="primary" icon={<Plus />} onClick={onAdd}>
              Ajouter une discipline
            </Button>
          )
        }
      />
      {stage.disciplines.length === 0 ? (
        <Empty
          title="Aucune discipline"
          description="Une étape notée comporte au moins une discipline."
        />
      ) : (
        <div className="tbl-wrap">
          <table className="tbl">
            <caption className="sr-only">Disciplines de l’étape {stage.label}</caption>
            <thead>
              <tr>
                <th scope="col">Discipline</th>
                <th scope="col">Notation</th>
                <th scope="col" className="num">
                  Barème
                </th>
                <th scope="col" className="num">
                  Coef.
                </th>
                <th scope="col" className="num">
                  Éliminatoire
                </th>
                <th scope="col">Correcteurs</th>
                <th scope="col">Absence</th>
                {editable && (
                  <th scope="col">
                    <span className="sr-only">Actions</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {stage.disciplines.map((d) => (
                <tr key={d.id}>
                  <td>
                    <strong>{d.label}</strong>
                    <div className="mono muted">{d.code}</div>
                  </td>
                  <td>
                    {GRADING_TYPES[d.grading]}
                    {!d.compensable && (
                      <div>
                        <Badge variant="warning">Non compensable</Badge>
                      </div>
                    )}
                  </td>
                  <td className="num">
                    {d.grading === 'status' ? '—' : `${fmt(d.minScore)} – ${fmt(d.maxScore)}`}
                  </td>
                  <td className="num">{d.grading === 'status' ? '—' : fmt(d.coefficient)}</td>
                  <td className="num">
                    {d.eliminatoryThreshold == null ? '—' : `< ${fmt(d.eliminatoryThreshold)}`}
                  </td>
                  <td>{d.evaluators}</td>
                  <td>{ABSENCE_POLICIES[d.absence]}</td>
                  {editable && (
                    <td>
                      <span className="row" style={{ gap: 4, flexWrap: 'nowrap' }}>
                        <Button
                          size="sm"
                          variant="ghost"
                          iconOnly
                          icon={<Pencil />}
                          aria-label={`Modifier ${d.label}`}
                          onClick={() => onEdit(d)}
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          iconOnly
                          icon={<Trash2 />}
                          aria-label={`Retirer ${d.label}`}
                          onClick={() => onRemove(d)}
                        />
                      </span>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th scope="row" colSpan={3}>
                  Total des coefficients
                </th>
                <td className="num">
                  <strong>{fmt(totalCoef)}</strong>
                </td>
                <td colSpan={editable ? 4 : 3} />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </Card>
  )
}
